import {
    type FC,
    type ReactNode,
    useEffect,
    useRef,
    useState,
} from 'react'

import { IoIosInformationCircleOutline } from 'react-icons/io'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
    Link,
    useLocation,
    useNavigate,
    useParams,
} from 'react-router-dom'

import { IconClose } from 'assets/svg/sprite'
import AvatarHolder from 'components/UI/AvatarHolder/AvatarHolder'
import { useDebounce } from 'helpers/hooks'
import {
    deleteCreatorsRecentSearch,
    getCreatorsRecentSearch,
} from 'services/endpoints/creator'
import {
    getMediaAutocomplete,
    storeCreatorRecentSearch
} from 'services/endpoints/media'
import { useAccessToken, useUserProfile } from 'store'

import NavigationBar from '../../components/UI/NavigationBar/NavigationBar'
import HeaderNavigation from '../HeaderNavigation/HeaderNavigation'

import './_header.scss'

interface RecentSearchUser {
    search_id: string
    id: string
    name: string
    username: string
    avatar: string | null
}
interface AutocompleteUser {
    id: string
    name: string
    username: string
    avatarSrc: string
}


interface HeaderProps {
    title: string | ReactNode
    headerNav?: string[]
    hideBackButton?: boolean
    rightElement?: ReactNode
    linkBack?: () => void
}

const CURRENT_APP_PATH_KEY = 'current-app-path'
const CURRENT_APP_HISTORY_INDEX_KEY = 'current-app-history-index'

const CHAT_RETURN_PATH_KEY = 'chat-return-path'
const CHAT_RETURN_HISTORY_INDEX_KEY = 'chat-return-history-index'

const isChatPath = (path: string): boolean => {
    const pathname = path.split('?')[0].split('#')[0]

    return (
        pathname === '/chat' ||
        pathname.startsWith('/chat/')
    )
}

const getHistoryIndex = (): number => {
    const index = window.history.state?.idx

    return typeof index === 'number'
        ? index
        : 0
}

const getStoredHistoryIndex = (
    key: string
): number | null => {
    const value = sessionStorage.getItem(key)

    if (value === null) {
        return null
    }

    const parsedValue = Number(value)

    return Number.isFinite(parsedValue)
        ? parsedValue
        : null
}

const Header = ({
    title,
    headerNav,
    hideBackButton,
    rightElement,
    linkBack,
}: HeaderProps) => {
    const navigate = useNavigate()
    const params = useParams()
    const location = useLocation()
    const isAuthenticated = useAccessToken() !== ''
    const queryClient = useQueryClient()
    const userData = useUserProfile()

    const [creatorSearch, setCreatorSearch] = useState('')
    const [creatorSearchOpen, setCreatorSearchOpen] = useState(false)

    const creatorSearchRef = useRef<HTMLDivElement>(null)

    const debouncedCreatorSearch = useDebounce(
        creatorSearch,
        500
    )

    const { data: autocompleteData } = useQuery<{
        data: {
            users: AutocompleteUser[]
        }
    }>(
        [
            'headerCreatorsAutocomplete',
            debouncedCreatorSearch
        ],
        async () =>
            await getMediaAutocomplete({
                searchTerm: debouncedCreatorSearch
            }),
        {
        enabled:
            isAuthenticated &&
            creatorSearchOpen &&
            !!debouncedCreatorSearch.trim()
        }
    )

    const autocompleteResults =
        autocompleteData?.data?.users ?? []

    const {
        data: recentSearches,
        refetch: refetchRecentSearches
    } = useQuery(
        'creators-recent-search',
        getCreatorsRecentSearch,
        {
            enabled: isAuthenticated && creatorSearchOpen
        }
    )

    const removeSearchResult = useMutation(
        async (idArray: string[]) => {
            await deleteCreatorsRecentSearch({
                search_id: idArray
            })
        },
        {
            onMutate: async idArray => {
                await queryClient.cancelQueries('creators-recent-search')

                const previous =
                    queryClient.getQueryData<RecentSearchUser[]>(
                        'creators-recent-search'
                    )

                queryClient.setQueryData<RecentSearchUser[]>(
                    'creators-recent-search',
                    old =>
                        (old ?? []).filter(
                            item => !idArray.includes(item.search_id)
                        )
                )

                return { previous }
            },

            onError: (_error, _ids, context) => {
                if (context?.previous) {
                    queryClient.setQueryData(
                        'creators-recent-search',
                        context.previous
                    )
                }
            }
        }
    )
    const filteredRecentSearches: RecentSearchUser[] =
        recentSearches ?? []

    const showRecentSearches =
        creatorSearchOpen &&
        !creatorSearch.trim() &&
        filteredRecentSearches.length > 0

    const showAutocomplete =
        creatorSearchOpen &&
        !!creatorSearch.trim() &&
        autocompleteResults.length > 0

    const showCreatorDropdown =
        showRecentSearches ||
        showAutocomplete

const recordCreatorSearch = (user: AutocompleteUser) => {
    storeCreatorRecentSearch({
        query: user.username,
        response: [
            {
                id: user.id,
                name: user.name,
                username: user.username,
                avatar: user.avatarSrc
            }
        ]
    })
        .then(() => {
            queryClient.invalidateQueries('creators-recent-search')
        })
        .catch(() => {
            // Recent search history should never block navigation.
        })
}


    const isDesktop = window.innerWidth >= 950

    const currentPath =
        `${location.pathname}` +
        `${location.search}` +
        `${location.hash}`

    const currentHistoryIndex = getHistoryIndex()

    const currentRouteIsChat = isChatPath(
        location.pathname
    )

    const desktopConversationView =
        isDesktop &&
        location.pathname.startsWith('/chat/')

    const [chatReturnPath, setChatReturnPath] =
        useState<string | null>(() => {
            return sessionStorage.getItem(
                CHAT_RETURN_PATH_KEY
            )
        })

    const [
        chatReturnHistoryIndex,
        setChatReturnHistoryIndex,
    ] = useState<number | null>(() => {
        return getStoredHistoryIndex(
            CHAT_RETURN_HISTORY_INDEX_KEY
        )
    })

    useEffect(() => {
        const previousPath = sessionStorage.getItem(
            CURRENT_APP_PATH_KEY
        )

        const previousHistoryIndex =
            getStoredHistoryIndex(
                CURRENT_APP_HISTORY_INDEX_KEY
            )

        const previousRouteWasChat =
            previousPath !== null &&
            isChatPath(previousPath)

        /*
         * The user has just entered chat from a route
         * outside chat.
         *
         * Save both the original route and its history
         * index. Moving between conversations will not
         * overwrite these values.
         */
        if (
            currentRouteIsChat &&
            previousPath !== null &&
            !previousRouteWasChat &&
            previousHistoryIndex !== null &&
            currentHistoryIndex > previousHistoryIndex
        ) {
            sessionStorage.setItem(
                CHAT_RETURN_PATH_KEY,
                previousPath
            )

            sessionStorage.setItem(
                CHAT_RETURN_HISTORY_INDEX_KEY,
                String(previousHistoryIndex)
            )

            setChatReturnPath(previousPath)
            setChatReturnHistoryIndex(
                previousHistoryIndex
            )
        }

        /*
         * Direct visit to chat.
         */
        if (
            currentRouteIsChat &&
            currentHistoryIndex <= 0
        ) {
            sessionStorage.removeItem(
                CHAT_RETURN_PATH_KEY
            )

            sessionStorage.removeItem(
                CHAT_RETURN_HISTORY_INDEX_KEY
            )

            setChatReturnPath(null)
            setChatReturnHistoryIndex(null)
        }

        /*
         * Once outside chat, clear stale chat return info.
         */
        if (!currentRouteIsChat) {
            sessionStorage.removeItem(
                CHAT_RETURN_PATH_KEY
            )

            sessionStorage.removeItem(
                CHAT_RETURN_HISTORY_INDEX_KEY
            )

            setChatReturnPath(null)
            setChatReturnHistoryIndex(null)
        }

        sessionStorage.setItem(
            CURRENT_APP_PATH_KEY,
            currentPath
        )

        sessionStorage.setItem(
            CURRENT_APP_HISTORY_INDEX_KEY,
            String(currentHistoryIndex)
        )
    }, [
        currentPath,
        currentRouteIsChat,
        currentHistoryIndex,
    ])

    const hasInternalHistory =
        currentHistoryIndex > 0

    const hasDesktopChatReturnRoute =
        chatReturnPath !== null &&
        chatReturnHistoryIndex !== null &&
        currentHistoryIndex >
            chatReturnHistoryIndex

    const shouldShowBackButton =
        !hideBackButton &&
        (
            desktopConversationView
                ? hasDesktopChatReturnRoute
                : hasInternalHistory
        )

    const handleBack = () => {
        if (desktopConversationView) {
            if (
                chatReturnPath === null ||
                chatReturnHistoryIndex === null
            ) {
                return
            }

            const historyDelta =
                chatReturnHistoryIndex -
                currentHistoryIndex

            if (historyDelta < 0) {
                navigate(historyDelta)
                return
            }

            navigate(chatReturnPath, {
                replace: true,
            })

            return
        }

        if (!hasInternalHistory) {
            return
        }

        if (linkBack) {
            linkBack()
            return
        }

        navigate(-1)
    }

    const hideHeaderNav =
        location.pathname.includes('/bank-information') ||
        location.pathname === '/settings/general/chat-settings/welcome-message'

const shouldShowHeaderNav =
    !hideHeaderNav &&
    !!headerNav &&
    headerNav.length > 0 &&
    (
        location.pathname.startsWith('/earnings') ||
        location.pathname.startsWith('/analytics') ||
        location.pathname.startsWith('/settings') ||
        (
            location.pathname.startsWith('/how-to') &&
            userData?.role !== 'fan'
        )
    )
    const shouldShowRightElement =
        rightElement &&
        !location.pathname.includes(
            `/chat/${params.id}`
        )

    const shouldShowChatMenuIcon =
        /^\/chat\/[^/]+$/.test(
            location.pathname
        )

    const InfoCircleIcon =
        IoIosInformationCircleOutline as FC<{
            size?: number | string
            color?: string
            className?: string
        }>
    useEffect(() => {
        if (!creatorSearchOpen) return

        const handleOutsideClick = (
            event: MouseEvent
        ) => {
            const target = event.target as Node

            if (
                creatorSearchRef.current &&
                !creatorSearchRef.current.contains(target)
            ) {
                setCreatorSearchOpen(false)
            }
        }

        document.addEventListener(
            'mousedown',
            handleOutsideClick
        )

        return () => {
            document.removeEventListener(
                'mousedown',
                handleOutsideClick
            )
        }
    }, [creatorSearchOpen])
    return (
        <header
            className={`header ${
                shouldShowHeaderNav
                    ? 'header--withnav'
                    : 'header--withoutnav'
            }`}
        >
            <div className="logo-container">
                <Link to="">
                    <img
                        src="/logo.png"
                        alt="logo"
                        className="logo-img"
                    />

                    <span className="logo-text">
                        <img
                        src="/af-dark.svg"
                        alt="AllFans"
                        style={{ height: '1.6rem', width: 'auto' }}
                        />
                    </span>
                </Link>
            </div>

            <div className="header--content">
                <div className="header__top-row">
                    {shouldShowBackButton && (
                        <div
                            className="header__linkback"
                            onClick={handleBack}
                            role="button"
                            tabIndex={0}
                            aria-label="Go back"
                            onKeyDown={event => {
                                if (event.key === 'Enter' && creatorSearch.trim()) {
                                    event.preventDefault()

                                    const searchTerm = creatorSearch.trim().replace(/^@/, '')

                                    const matchedUser = autocompleteResults.find(user =>
                                        user.username.toLowerCase() === searchTerm.toLowerCase() ||
                                        user.name.toLowerCase() === searchTerm.toLowerCase()
                                    )

                                    if (matchedUser) {
                                        recordCreatorSearch(matchedUser)

                                        setCreatorSearch('')
                                        setCreatorSearchOpen(false)

                                        navigate(`/${matchedUser.username}`)
                                        return
                                    }

                                    setCreatorSearch('')
                                    setCreatorSearchOpen(false)

                                    navigate(`/${searchTerm}`)
                                }

                                if (event.key === 'Escape') {
                                    setCreatorSearchOpen(false)
                                }
                            }}
                        />
                    )}

                    {/*
                     * DESKTOP:
                     * - nav pages show NavigationBar on left
                     * - normal pages show title on left
                     *
                     * MOBILE:
                     * - title remains here
                     * - NavigationBar stays in secondary row below
                     */}
                    <div className="header__desktop-left">
                        {shouldShowHeaderNav ? (
                            <div className="header__desktop-nav">
                                <NavigationBar
                                    navArr={headerNav}
                                    customClass="header"
                                    type="dark"
                                />
                            </div>
                        ) : (
                            <h1 className="header__title">
                                {desktopConversationView
                                    ? 'Messages'
                                    : title}
                            </h1>
                        )}

                        <h1 className="header__title header__title--mobile">
                            {desktopConversationView
                                ? 'Messages'
                                : title}
                        </h1>
                    </div>

                {isAuthenticated && (
                    <div
                        ref={creatorSearchRef}
                        className={`header__creator-search ${showCreatorDropdown ? 'header__creator-search--open' : ''}`}
                    >
                        <div className="header__creator-search-input">
                            <span className="header__creator-search-icon" />

                            <input
                                type="text"
                                value={creatorSearch}
                                placeholder="Search Creators"
                                aria-label="Search Creators"
                                autoComplete="off"
                                onFocus={() => {
                                    setCreatorSearchOpen(true)
                                    refetchRecentSearches()
                                }}
                                onChange={event => {
                                    setCreatorSearch(event.target.value)
                                    setCreatorSearchOpen(true)
                                }}
                                onKeyDown={event => {
                                    if (event.key === 'Enter' && creatorSearch.trim()) {
                                        event.preventDefault()

                                        navigate(`/creators?search=${encodeURIComponent(creatorSearch.trim())}`)
                                        setCreatorSearchOpen(false)
                                    }

                                    if (event.key === 'Escape') {
                                        setCreatorSearchOpen(false)
                                    }
                                }}
                            />

                            {!!creatorSearch && (
                                <button
                                    type="button"
                                    className="header__creator-search-clear-input"
                                    onMouseDown={event => {
                                        event.preventDefault()
                                    }}
                                    onClick={() => {
                                        setCreatorSearch('')
                                        setCreatorSearchOpen(true)
                                    }}
                                    aria-label="Clear creator search"
                                >
                                    <IconClose />
                                </button>
                            )}
                        </div>

                        {showCreatorDropdown && (
                            <div className="header__creator-search-dropdown">
                                {showAutocomplete ? (
                                    <div className="header__creator-search-results">
                                        {autocompleteResults.map(user => (
                                            <div
                                                key={user.id}
                                                className="header__creator-search-result"
                                                onMouseDown={event => {
                                                    event.preventDefault()

                                                    recordCreatorSearch(user)

                                                    setCreatorSearchOpen(false)
                                                    setCreatorSearch('')

                                                    navigate(`/${user.username}`)
                                                }}
                                            >
                                                <AvatarHolder
                                                    img={user.avatarSrc}
                                                    size="36"
                                                    userId={user.id}
                                                />

                                                <div className="header__creator-search-user">
                                                    <div className="header__creator-search-name">
                                                        {user.name}
                                                    </div>

                                                    <div className="header__creator-search-username">
                                                        @{user.username}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <>
                                        <div className="header__creator-search-heading">
                                            <span>Recent Searches</span>

                                            <button
                                                type="button"
                                                className="header__creator-search-clear"
                                                onMouseDown={event => {
                                                    event.preventDefault()
                                                    event.stopPropagation()

                                                    const ids = filteredRecentSearches.map(item => item.search_id)

                                                    if (ids.length) {
                                                        removeSearchResult.mutate(ids)
                                                    }
                                                }}
                                            >
                                                Clear
                                            </button>
                                        </div>

                                        <div className="header__creator-search-separator" />

<div className="header__creator-search-results">
    {filteredRecentSearches.map(data => (
        <div
            key={data.search_id}
            className="header__creator-search-result"
            onMouseDown={event => {
                event.preventDefault()
                setCreatorSearchOpen(false)
                navigate(`/${data.username}`)
            }}
        >
            <AvatarHolder
                img={data.avatar ?? ''}
                size="36"
                userId={data.id}
            />

            <div className="header__creator-search-user">
                <div className="header__creator-search-name">
                    {data.name}
                </div>

                <div className="header__creator-search-username">
                    @{data.username}
                </div>
            </div>

            <button
                type="button"
                className="header__creator-search-remove"
                onMouseDown={event => {
                    event.preventDefault()
                    event.stopPropagation()

                    removeSearchResult.mutate([data.search_id])
                }}
                aria-label="Remove recent search"
            >
                <IconClose />
            </button>
        </div>
    ))}
</div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                )}

                    {shouldShowChatMenuIcon ? (
                        <div
                            className="header__right--info"
                            onClick={() => {
                                if (isDesktop) {
                                    return
                                }

                                window.dispatchEvent(
                                    new CustomEvent(
                                        'toggle-chat-header-section'
                                    )
                                )
                            }}
                        >
                            <InfoCircleIcon
                                size={25}
                            />
                        </div>
                    ) : (
                        shouldShowRightElement && (
                            <div className="header__right">
                                {rightElement}
                            </div>
                        )
                    )}
                </div>

                {/*
                 * Mobile keeps the existing second-row nav.
                 * CSS hides this on desktop.
                 */}
                {shouldShowHeaderNav && (
                    <div className="header__nav-row header__nav-row--active">
                        <NavigationBar
                            navArr={headerNav}
                            customClass="header"
                            type="dark"
                        />
                    </div>
                )}
            </div>

            <div
                className="header__corner-cutouts"
                aria-hidden="true"
            >
                <svg
                    className="header__corner-cutout header__corner-cutout--left"
                    viewBox="0 0 32 32"
                >
                    <path d="M0 0H32C14.3269 0 0 14.3269 0 32V0Z" />
                </svg>

                <svg
                    className="header__corner-cutout header__corner-cutout--right"
                    viewBox="0 0 32 32"
                >
                    <path d="M32 0H0C17.6731 0 32 14.3269 32 32V0Z" />
                </svg>
            </div>

            <HeaderNavigation />
        </header>
    )
}

export default Header